import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startGoogleLogin } from "@/pages/landing/primitives";
import { getStoredPlanSelection } from "@/lib/planSelection";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// AuthCallback runs when the browser lands at `<origin>/*#session_id=...`
// after the Emergent-managed Google OAuth roundtrip. It exchanges the
// one-shot session_id via `POST /api/auth/google`, opens our own
// `session_token` cookie, and continues the user's original intent:
//
//   1. Team-invite deep-link → follow `iti.postLoginRedirect`
//   2. New user with a stashed prompt (`iti.initialBusinessIdea` /
//      `iti.pendingPrompt`) → `/onboarding` (the onboarding page reads
//      those keys and kicks off quickstart automatically).
//   3. Returning user with a business → `/dashboard`.
//   4. Everyone else → `/onboarding`.
//
// FAILURE MODE (this is the fix delivered with the Feb-2026 auth bug):
// Previously, ANY failure in the exchange silently bounced the user
// back to `/`, so a legitimate signup attempt would look like it had
// simply been ignored. Now the callback renders a visible error card
// with a retry button + a "Back to home" escape hatch. The user's
// pending prompt is preserved in localStorage the whole time.

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);   // { title, detail }
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const session_id = m ? decodeURIComponent(m[1]) : null;
    if (!session_id) {
      setError({
        title: "We couldn't complete sign-in",
        detail: "The Google sign-in link didn't include a session token. This usually happens if you refreshed the page after signing in — try signing in again.",
      });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google", { session_id });
        setUser(data);
        // Clear hash so a reload doesn't try to consume the same
        // (now-invalid) session_id.
        window.history.replaceState(null, "", window.location.pathname);
        // Honor a stashed redirect (team invite accept page, etc.)
        const stashedNext = localStorage.getItem("iti.postLoginRedirect");
        if (stashedNext) {
          localStorage.removeItem("iti.postLoginRedirect");
          navigate(stashedNext, { replace: true });
          return;
        }
        // If the user has a stashed website prompt, ALWAYS route to
        // /onboarding — the Onboarding page reads
        // `iti.initialBusinessIdea` (or `iti.pendingPrompt`) and
        // triggers the quickstart automatically.
        let pendingPrompt = "";
        try {
          pendingPrompt = localStorage.getItem("iti.pendingPrompt") || localStorage.getItem("iti.initialBusinessIdea") || "";
        } catch { /* private mode */ }
        // A stashed paid plan selection (plan + interval from the landing
        // pricing cards) also routes to /onboarding so it can be consumed
        // into Stripe checkout instead of going stale.
        if (pendingPrompt.trim() || getStoredPlanSelection()) {
          navigate("/onboarding", { replace: true });
          return;
        }
        // Otherwise, check if the user already has a business.
        try {
          const { data: biz } = await api.get("/business");
          if (biz && biz.business_id) navigate("/dashboard", { replace: true });
          else navigate("/onboarding", { replace: true });
        } catch {
          navigate("/onboarding", { replace: true });
        }
      } catch (e) {
        console.error("Auth failed", e);
        const status = e?.response?.status;
        const backendMsg = e?.response?.data?.detail;
        const isExpired = status === 401;
        setError({
          title: isExpired
            ? "Your Google sign-in link expired"
            : "We couldn't complete sign-in",
          detail: backendMsg || "Something went wrong exchanging your Google session. Please try again.",
        });
      }
    })();
  }, [navigate, setUser]);

  const retry = () => {
    if (retrying) return;
    setRetrying(true);
    // Clear the stale hash before starting a fresh OAuth roundtrip so
    // the browser doesn't try to reconsume the expired session_id.
    try {
      window.history.replaceState(null, "", window.location.pathname);
    } catch { /* ignore */ }
    startGoogleLogin();
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0] px-6" data-testid="auth-callback-error">
        <div className="nb-card p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center">
            <AlertCircle className="w-7 h-7 text-red-500" strokeWidth={2.25} />
          </div>
          <div
            className="font-display text-2xl font-extrabold tracking-tight text-slate-900"
            data-testid="auth-callback-error-title"
          >
            {error.title}
          </div>
          <p
            className="text-sm text-slate-600 mt-3 leading-relaxed"
            data-testid="auth-callback-error-detail"
          >
            {error.detail}
          </p>
          <p className="text-xs text-slate-500 mt-3">
            Good news: your website request is still saved. As soon as you sign in, we&apos;ll pick up right where you left off.
          </p>
          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={retry}
              disabled={retrying}
              data-testid="auth-callback-retry"
              className="w-full iti-btn-primary px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Try signing in again
            </button>
            <button
              type="button"
              onClick={() => navigate("/", { replace: true })}
              data-testid="auth-callback-back-home"
              className="w-full text-sm text-slate-600 hover:text-slate-900 inline-flex items-center justify-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0]">
      <div className="nb-card p-8 text-center" data-testid="auth-callback-loading">
        <img src="/iti-logo.png" alt="iTi" className="w-16 h-16 mx-auto mb-3 rounded-lg border-2 border-black bg-white p-1 object-contain" />
        <div className="font-display text-2xl font-extrabold tracking-tight">Signing you in…</div>
        <p className="text-sm text-gray-600 mt-2">Just a moment.</p>
      </div>
    </div>
  );
}
